import { API_ENDPOINTS } from '@common/constants/apiEndpoint';
import { Method, PAGE_MAXIMUM_ITEM } from '@common/constants/shared';
import { ComponentType, IObj, IRecord } from '@common/types';
import * as _ from 'lodash';
import axiosInstance from './axiosService';

export const pickStringObject = (value: any = {}) => {
  const data = _.pick(value, [
    'loggedInUser',
    'routeParam',
    'currentListIds',
    'valueInputs',
    'timeZone',
    'locale',
    'currentLocation',
    'createRecordId',
    'sortedItemNumber',
    'idUnivaPaySubscription',
    'customActions',
    'web3Config',
    // 'appInfor',
  ]);
  const appInfor = _.pick(value.appInfor, ['APP_NAME', 'APP_VERSION']);

  return JSON.stringify(Object.assign(data, { appInfor }));
};

export const stringObject = (value: any = {}) => JSON.stringify(value);

export const fetchBindingValue = async (
  data: any,
  dependencies: any,
  controller?: any
) => {
  const response = await axiosInstance({
    method: 'post',
    url: 'v3/request-api',
    params: {
      appId: dependencies.appInfor.id,
    },
    data: {
      method: Method.CREATE,
      service: API_ENDPOINTS.CONVERT_VALUE,
      query: {
        clientValues: pickStringObject(dependencies),
      },
      data: JSON.stringify(data),
    },
    ...(controller && { signal: controller.signal }),
  });

  return response.data;
};

// binding screen
export const getBindingScreen = async ({
  appId,
  dependencies,
  screenUuid,
  controller,
  objectIds,
}: {
  appId: string;
  dependencies: Record<string, any>;
  screenUuid: string;
  controller?: any;
  objectIds?: string[];
}) => {
  const response = await axiosInstance({
    method: 'get',
    url: API_ENDPOINTS.BINDING_SCREEN,
    params: {
      appId: appId,
      clientValues: pickStringObject(dependencies),
      screenUuid,
      ...(objectIds && { objectIds: JSON.stringify(objectIds) }),
    },

    ...(controller && { signal: controller.signal }),
  });

  return response.data;
};

export const getRecordById = async (
  appId: string,
  databaseUuid: string,
  recordId: string
) => {
  const response = await axiosInstance({
    method: 'get',
    url: `v3/database/${databaseUuid}/records/${recordId}`,
    params: {
      appId,
    },
  });
  return response.data;
};

// binding component
export const fetchBindingComponent = async ({
  appId,
  dependencies,
  screenUuid,
  controller,
  idComponent,
  page = 1,
  limit,
  filterText,
}: {
  appId: string;
  dependencies: Record<string, any>;
  screenUuid: string;
  idComponent: string;
  controller?: any;
  page?: number;
  limit?: number;
  filterText?: string;
}) => {
  const response = await axiosInstance({
    method: 'get',
    url: `${API_ENDPOINTS.BINDING_SCREEN}/${idComponent}`,
    params: {
      appId: appId,
      clientValues: pickStringObject(dependencies),
      screenUuid,
      page,
      ...(limit && { limit }),
      ...(filterText && { search: filterText }),
    },
    ...(controller && { signal: controller.signal }),
  });

  return response.data;
};

export const fetchRecordList = async ({
  obj,
  controller,
  page,
  dataBinding,
  dependencies,
}: {
  obj: IObj;
  controller?: any;
  page: number;
  dataBinding: IObj;
  dependencies: IObj;
}) => {
  const tableId = _.get(dataBinding, 'source.tableId');
  const { appInfor } = dependencies;

  const { pageSize = 100, limit } = dataBinding?.source?.options || {};

  const objectType = _.get(obj, 'type');

  const isWeb3List = objectType === ComponentType.web3List;

  const response = await fetchBindingComponent({
    appId: appInfor.id,
    dependencies,
    screenUuid: obj?.screenUuid,
    controller,
    idComponent: obj.id,
    limit: pageSize * page,
  });

  if (Array.isArray(response?.data)) {
    const reverse = _.get(obj, 'dataBinding.source.options.reverse', false);

    const formatRecord = response.data.map((record: IRecord) => ({
      ...record,
      _meta: {
        componentId: obj.id,
        tableId: tableId,
        datasourceId: obj.initId || obj.id,
        databindingId: dataBinding?.id,
        ...(isWeb3List && {
          web3Config: _.pick(response, [
            'address',
            'token_address',
            'network',
            'apiToken',
          ]),
        }),
      },
    }));
    const recordAPi = reverse ? formatRecord.reverse() : formatRecord;

    const limitPage = _.ceil(limit / pageSize);
    const responseTotal = Number(response.total);

    const dataTotalPage = _.ceil(responseTotal / pageSize);

    const totalPage =
      limit && limitPage <= dataTotalPage ? limitPage : dataTotalPage;

    const total = !limit
      ? responseTotal
      : responseTotal < limit
      ? responseTotal
      : limit;

    return { total, data: recordAPi, totalPage, limit };
  }

  return response;
};

export const fetchScreenAction = async ({
  clientValues,
  screenUuid,
  actionId,
  objectId,
  screenAction = false,
  data,
}: {
  clientValues: IObj;
  screenUuid: string;
  data?: IObj;
  actionId: string;
  objectId?: string;
  screenAction?: boolean;
}) => {
  const response = await axiosInstance({
    method: 'post',
    url: API_ENDPOINTS.ACTION_SCREEN,
    params: {
      appId: clientValues.appInfor?.id,
      screenUuid,
      actionId,
      ...(!screenAction && { objectId }),
      ...(screenAction && { screenAction }),
      clientValues: pickStringObject(clientValues),
    },
    ...(data && { data }),
  });

  return response.data;
};

// get list object
export const getListOptions = async ({
  appId,
  fieldId,
  screenUuid,
  objectId,
  dependencies,
}: {
  appId: string;
  fieldId: string;
  screenUuid: string;
  objectId: string | undefined;
  dependencies: object;
}) => {
  const response = await axiosInstance({
    method: 'get',
    url: `${API_ENDPOINTS.DATABASE_LIST_OPTION}/${fieldId}`,
    params: {
      screenUuid,
      objectId,
      appId,
      clientValues: pickStringObject(dependencies),
    },
  });

  return response.data;
};

// payment compnents
export type StripePaymentParams = {
  appId: string;
  screenUuid: string;
  objectId: string;
  dependencies: any;
  destination: string;
  paymentMethodId: string;
  subscription: boolean;
};
export const stripePayment = async (params: StripePaymentParams) => {
  const {
    screenUuid,
    appId,
    objectId,
    dependencies,
    destination,
    paymentMethodId,
    subscription,
  } = params;

  const { data }: any = await axiosInstance({
    method: 'post',
    url: 'v3/payment-intent',
    data: {
      destination,
      paymentMethodId,
      subscription,
    },
    params: {
      screenUuid,
      appId,
      objectId,
      clientValues: pickStringObject(dependencies),
    },
  });

  return data;
};

export type UnivaPayParams = {
  paymentType: string;
  period: string;
  type: string;
  dataCard: {
    cardholder: string;
    card_number: string;
    exp_month: string;
    exp_year: string;
    cvv: string;
  };
  appId: string;
  screenId: string;
  objectId: string;
  saveCardOptions: { saveCard: boolean; onlySaveCard: boolean };
  dependencies: any;
};

export const univaPayPayment = async (params: UnivaPayParams) => {
  const {
    screenId,
    appId,
    objectId,
    dataCard,
    paymentType,
    period,
    type,
    saveCardOptions,
    dependencies,
  } = params;

  const { data }: any = await axiosInstance({
    method: 'post',
    url: 'v3/payment-univapay-component',
    data: {
      dataCard,
      paymentType,
      period,
      type,
      saveCardOptions,
    },
    params: {
      screenId,
      appId,
      objectId,
      clientValues: pickStringObject(dependencies),
    },
  });

  return data;
};
